import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAnchoredList } from '../../components/ui/useAnchoredList';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { prices } from '../stock/api';

/**
 * What this part has cost before, shown against what is being paid now.
 *
 * The question a price on an order raises is always the same one — is this a
 * good price? — and the answer lives on a different screen entirely. Hovering
 * is the right moment for it: the figure is already under the pointer, and
 * nobody is going to open the part's card to check a number they are only
 * mildly curious about.
 *
 * Fetched on first hover, never before. This appears on every line of every
 * order, and asking for the price history of forty parts to draw a list nobody
 * has pointed at yet is forty requests for nothing.
 */
export function PriceHint({ componentId, children }: { componentId: string; children: ReactNode }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const position = useAnchoredList(anchor, open);

  // Behind the cost permission, same as the endpoint: without it the request
  // would only ever answer 403, and a card that always says "no" is worse than
  // no card.
  const allowed = can('cost.read');

  const history = useQuery({
    queryKey: ['stock', 'prices', componentId],
    queryFn: () => prices(componentId),
    enabled: allowed && open,
    staleTime: 5 * 60_000,
  });

  if (!allowed) return <>{children}</>;

  const data = history.data;
  const nothing = data && !data.last_unit_cost && data.averages.length === 0;

  return (
    <span
      ref={anchor}
      className="price-hint"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      // A finger has no hover: on a phone the figure is tapped instead.
      onClick={() => setOpen((was) => !was)}
    >
      {children}
      {open && (
        <span className="price-hint__card" style={position} role="tooltip">
          <strong>{t('purchases.priceHint.title')}</strong>
          {history.isLoading && <em className="hint">{t('common.loading')}</em>}
          {nothing && <em className="hint">{t('purchases.priceHint.never')}</em>}
          {data?.last_unit_cost && (
            <span className="price-hint__row">
              <em>{t('purchases.priceHint.last')}</em>
              <b>
                {num(data.last_unit_cost)} {data.last_currency}
              </b>
              {data.last_at && (
                <em className="hint">{new Date(data.last_at).toLocaleDateString()}</em>
              )}
            </span>
          )}
          {(data?.averages ?? []).map((row) => (
            <span className="price-hint__row" key={row.currency}>
              <em>{t('purchases.priceHint.average')}</em>
              <b>
                {num(row.average)} {row.currency}
              </b>
              <em className="hint">
                {t('purchases.priceHint.over', { n: row.receipts, qty: num(row.quantity) })}
              </em>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
